import { useCallback, useEffect, useState } from "react";

import { Edit as EditIcon } from "@mui/icons-material";
import { Button, Chip, IconButton, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import {
  AddPaymentMethodFormDialog,
  EditPaymentMethodFormDialog,
} from "@/features/payment-methods";
import { PermissionEnum } from "@/features/permissions";
import { apiClient, statusColors, statusLabels, getErrorMessage } from "@/features/shared";
import { SearchTextField, useSnackbar } from "@/features/ui";
import { useSearchFilter, useServerPagination } from "@/hooks";

export const PaymentMethods = () => {
  const { hasPermission } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);

  const { setRowCount, paginationParams, dataGridPaginationProps, resetParams } =
    useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });

  const loadPaymentMethods = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiClient.get("payment-methods", {
        params: {
          ...paginationParams,
          ...(keyword && { name: keyword }),
          order: "ASC",
        },
      });
      setPaymentMethods(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat metode pembayaran"));
    } finally {
      setLoading(false);
    }
  }, [paginationParams, keyword, setRowCount, showSnackbar]);

  useEffect(() => {
    loadPaymentMethods();
  }, [loadPaymentMethods]);

  const toolbarContent = hasPermission(PermissionEnum.CREATE_PAYMENT_METHODS) && (
        <Button
          variant="contained"
          onClick={() => setIsAddDialogOpen(true)}
        >
          Tambah
        </Button>
      );

  return (
    <PageContainer maxWidth="xl" breadcrumbs={[]} slotProps={{ header: { slotProps: { toolbar: { children: toolbarContent } } } }}>
      <Stack spacing={3}>
        <Grid container sx={{ justifyContent: "flex-end" }}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari nama metode pembayaran"
              {...searchProps}
            />
          </Grid>
        </Grid>
        <DataGrid
          rows={paymentMethods}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "name",
              headerName: "Nama",
              flex: 1,
              sortable: false,
            },
            {
              field: "status",
              headerName: "Status",
              flex: 1,
              sortable: false,
              renderCell: ({ row }) => (
                <Chip
                  label={statusLabels[row.status]}
                  color={statusColors[row.status]}
                />
              ),
            },
            ...(hasPermission(PermissionEnum.UPDATE_PAYMENT_METHODS)
              ? [
                  {
                    field: "actions",
                    type: "actions",
                    headerName: "Aksi",
                    getActions: (params) => [
                      <IconButton
                        key="edit"
                        onClick={() => setPaymentMethodId(params.id)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>,
                    ],
                  },
                ]
              : []),
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
        <AddPaymentMethodFormDialog
          isOpen={isAddDialogOpen}
          onClose={() => setIsAddDialogOpen(false)}
          onSubmitted={loadPaymentMethods}
        />
        <EditPaymentMethodFormDialog
          isOpen={!!paymentMethodId}
          paymentMethodId={paymentMethodId}
          onClose={() => setPaymentMethodId(null)}
          onSubmitted={loadPaymentMethods}
        />
      </Stack>
    </PageContainer>
  );
};
